import { useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router';
import type { MealPlanDto, MealSlot } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { buttonClass } from '../../components/Button';
import { SparkleIcon } from '../../components/icons';
import { Skeleton } from '../../components/Skeleton';
import { AddMealDialog } from '../../components/plans/AddMealDialog';
import { MonthView } from '../../components/plans/MonthView';
import { MovePlanDialog } from '../../components/plans/MovePlanDialog';
import { PlanColorDot } from '../../components/plans/PlanColorDot';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import {
  addDays,
  addMonths,
  formatDayShort,
  formatWeekLabel,
  isIsoDate,
  monthGridRange,
  startOfMonth,
  startOfWeek,
  todayIso,
  weekDays,
  weekRange,
} from '../../lib/planDates';
import {
  householdServings,
  isLeftovers,
  LEFTOVERS_BADGE_CLASS,
  LEFTOVERS_LABEL,
  planHeadline,
  plansFor,
  SLOT_LABEL,
  SLOT_ORDER,
  statusBadgeClass,
  STATUS_LABEL,
} from '../../lib/plans';

/** Navigating away from a plan can hand us a confirmation to show once. */
interface PlanNavState {
  toast?: string;
}

type ViewMode = 'week' | 'month';
const VIEW_KEY = 'fewerbags:plan:view';

function loadView(): ViewMode {
  if (typeof localStorage === 'undefined') return 'week';
  try {
    return localStorage.getItem(VIEW_KEY) === 'month' ? 'month' : 'week';
  } catch {
    return 'week';
  }
}

function saveView(view: ViewMode) {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    // private mode — the choice simply doesn't persist
  }
}

type PlansListResponse = Awaited<ReturnType<typeof api.plans.list>>;

export function PlanWeekPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const qc = useQueryClient();
  const { auth } = useAuth();
  const defaultServings = householdServings(auth.household);

  const [view, setView] = useState<ViewMode>(loadView);
  const today = todayIso();

  const requestedWeek = params.get('week');
  const week = startOfWeek(isIsoDate(requestedWeek) ? requestedWeek : today);
  const { from: weekFrom, to: weekTo } = weekRange(week);
  const days = weekDays(week);

  const requestedMonth = params.get('month');
  const month = startOfMonth(isIsoDate(requestedMonth) ? requestedMonth : today);
  const { from: monthFrom, to: monthTo } = monthGridRange(month);

  const from = view === 'week' ? weekFrom : monthFrom;
  const to = view === 'week' ? weekTo : monthTo;

  const [adding, setAdding] = useState<{ date: string; slot: MealSlot } | null>(null);
  const [moving, setMoving] = useState<MealPlanDto | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const toast = (location.state as PlanNavState | null)?.toast;

  const listKey = queryKeys.plans.week(from, to);
  const plans = useQuery({
    queryKey: listKey,
    queryFn: () => api.plans.list(from, to),
    placeholderData: (previous) => previous,
  });

  const all = plans.data?.plans ?? [];

  function changeView(next: ViewMode) {
    setView(next);
    saveView(next);
  }

  function goToWeek(iso: string) {
    setParams(iso === startOfWeek(today) ? {} : { week: iso }, { replace: false });
  }

  function goToMonth(iso: string) {
    setParams(iso === startOfMonth(today) ? {} : { month: iso }, { replace: false });
  }

  const move = useMutation({
    mutationFn: ({ plan, date, slot }: { plan: MealPlanDto; date: string; slot: MealSlot }) =>
      api.plans.update(plan.id, { date, slot }),
    onMutate: async ({ plan, date, slot }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const previous = qc.getQueryData<PlansListResponse>(listKey);
      qc.setQueryData<PlansListResponse>(listKey, (old) =>
        old ? { plans: old.plans.map((p) => (p.id === plan.id ? { ...p, date, slot } : p)) } : old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(listKey, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
  });

  function movePlan(plan: MealPlanDto, date: string, slot: MealSlot) {
    if (plan.date === date && plan.slot === slot) return;
    move.mutate({ plan, date, slot });
  }

  function handleDrop(e: DragEvent, date: string, slot: MealSlot) {
    e.preventDefault();
    setDragOverKey(null);
    const planId = e.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    if (!planId) return;
    const plan = all.find((p) => p.id === planId);
    if (!plan) return;
    movePlan(plan, date, slot);
  }

  const navButton =
    'inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-line bg-surface px-3 text-sm font-semibold text-brand shadow-sm shadow-brand/5 transition-colors hover:bg-brand-soft active:scale-[0.97]';
  const viewToggleButton = (active: boolean) =>
    `min-h-9 rounded-lg px-3 text-sm font-semibold transition-colors ${
      active ? 'bg-brand text-white shadow-sm' : 'text-brand hover:bg-brand-soft'
    }`;

  return (
    <AppShell width="wide">
      <div className="flex flex-col gap-4 lg:gap-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <span aria-hidden className="h-8 w-1.5 shrink-0 rounded-full bg-brand" />
            <h1 className="text-2xl sm:text-3xl">Meal plan</h1>
            <Link to="/plan/week" className={buttonClass({ size: 'sm', block: false })}>
              <SparkleIcon className="size-4" /> Plan my week
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/plan/week/auto" className={buttonClass({ size: 'sm', block: false })}>
              <SparkleIcon className="size-4" /> Auto-plan
            </Link>
            <div
              className="flex gap-1 rounded-xl border border-line bg-surface p-1"
              role="group"
              aria-label="View"
            >
              <button
                type="button"
                className={viewToggleButton(view === 'week')}
                aria-pressed={view === 'week'}
                onClick={() => changeView('week')}
              >
                Week
              </button>
              <button
                type="button"
                className={viewToggleButton(view === 'month')}
                aria-pressed={view === 'month'}
                onClick={() => changeView('month')}
              >
                Month
              </button>
            </div>
            {view === 'week' ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={navButton}
                  onClick={() => goToWeek(addDays(week, -7))}
                  aria-label="Previous week"
                >
                  <span aria-hidden>←</span>
                </button>
                <button type="button" className={navButton} onClick={() => goToWeek(today)}>
                  Today
                </button>
                <button
                  type="button"
                  className={navButton}
                  onClick={() => goToWeek(addDays(week, 7))}
                  aria-label="Next week"
                >
                  <span aria-hidden>→</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={navButton}
                  onClick={() => goToMonth(addMonths(month, -1))}
                  aria-label="Previous month"
                >
                  <span aria-hidden>←</span>
                </button>
                <button type="button" className={navButton} onClick={() => goToMonth(today)}>
                  Today
                </button>
                <button
                  type="button"
                  className={navButton}
                  onClick={() => goToMonth(addMonths(month, 1))}
                  aria-label="Next month"
                >
                  <span aria-hidden>→</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {view === 'week' ? (
          <p className="text-base font-semibold text-brand" aria-live="polite">
            {formatWeekLabel(weekFrom, weekTo)}
            {plans.isFetching ? ' · updating…' : ''}
          </p>
        ) : null}

        {toast ? <Alert tone="success">{toast}</Alert> : null}
        {plans.isError ? <Alert tone="error">{messageForError(plans.error)}</Alert> : null}
        {move.isError ? <Alert tone="error">{messageForError(move.error)}</Alert> : null}

        {view === 'month' ? (
          <MonthView
            monthIso={month}
            plans={all}
            onSelectDay={(date) => {
              changeView('week');
              goToWeek(startOfWeek(date));
            }}
          />
        ) : (
          <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7 lg:gap-2.5">
            {days.map((date) => {
              const isTodayColumn = date === today;
              return (
                <li
                  key={date}
                  className={`card flex flex-col gap-2 overflow-hidden p-2.5 ${
                    isTodayColumn ? 'ring-2 ring-accent' : ''
                  }`}
                >
                  <h2
                    className={`-mx-2.5 -mt-2.5 mb-1 flex items-center justify-between px-3 py-2 text-sm ${
                      isTodayColumn ? 'bg-brand text-white' : 'bg-brand-soft/70 text-brand'
                    }`}
                  >
                    <span>{formatDayShort(date)}</span>
                    {isTodayColumn ? (
                      <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-ink">
                        Today
                      </span>
                    ) : null}
                  </h2>

                  {SLOT_ORDER.map((slot) => {
                    const cell = plansFor(all, date, slot);
                    const cellKey = `${date}|${slot}`;
                    // On phones an empty slot is hidden — the day's "Add meal"
                    // button covers it — while the week grid keeps its rows.
                    const hideWhenEmpty = cell.length === 0 ? 'hidden lg:block' : '';
                    return (
                      <div
                        key={slot}
                        className={`${hideWhenEmpty} rounded-lg ${
                          dragOverKey === cellKey ? 'bg-accent-soft ring-1 ring-accent' : ''
                        }`}
                        onDragOver={(e) => {
                          if (!draggingId) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDragOverKey(cellKey);
                        }}
                        onDragLeave={() => setDragOverKey((k) => (k === cellKey ? null : k))}
                        onDrop={(e) => handleDrop(e, date, slot)}
                      >
                        <h3 className="text-[0.7rem] font-semibold tracking-wide text-muted uppercase">
                          {SLOT_LABEL[slot]}
                        </h3>
                        <ul className="mt-1 flex flex-col gap-1">
                          {cell.map((plan) => (
                            <li
                              key={plan.id}
                              draggable
                              onDragStart={(e) => {
                                setDraggingId(plan.id);
                                e.dataTransfer.setData('text/plain', plan.id);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragEnd={() => {
                                setDraggingId(null);
                                setDragOverKey(null);
                              }}
                              className={draggingId === plan.id ? 'opacity-50' : ''}
                            >
                              <Link
                                to={`/plan/${plan.id}`}
                                className="block cursor-grab rounded-xl border border-line bg-surface p-2 shadow-sm shadow-brand/10 transition-[border-color,box-shadow] hover:border-brand/40 hover:shadow-md active:cursor-grabbing"
                              >
                                <span className="flex items-center gap-1.5">
                                  {plan.recipes.slice(0, 4).map((r) => (
                                    <PlanColorDot
                                      key={r.recipeId}
                                      colorIndex={r.colorIndex}
                                      label={r.title}
                                    />
                                  ))}
                                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                                    {planHeadline(plan)}
                                  </span>
                                </span>
                                <span className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted">
                                  {isLeftovers(plan) ? (
                                    <span
                                      className={`rounded-full px-1.5 py-0.5 font-semibold ${LEFTOVERS_BADGE_CLASS}`}
                                    >
                                      {LEFTOVERS_LABEL}
                                    </span>
                                  ) : null}
                                  {plan.serveAt ? <span>{plan.serveAt}</span> : null}
                                  <span>serves {plan.servings}</span>
                                  <span
                                    className={`rounded-full px-1.5 py-0.5 ${statusBadgeClass(plan.status)}`}
                                  >
                                    {STATUS_LABEL[plan.status]}
                                  </span>
                                </span>
                              </Link>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setMoving(plan);
                                }}
                                className="mt-0.5 min-h-8 rounded-md px-1 text-xs text-muted underline-offset-2 hover:text-brand hover:underline"
                              >
                                Move…
                              </button>
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          onClick={() => setAdding({ date, slot })}
                          className="mt-1 hidden min-h-9 w-full rounded-lg border border-dashed border-line text-xs text-muted transition-colors hover:border-brand/50 hover:bg-brand-soft/50 hover:text-brand lg:block"
                        >
                          + Add {SLOT_LABEL[slot].toLowerCase()}
                        </button>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => setAdding({ date, slot: 'dinner' })}
                    className="min-h-11 w-full rounded-xl border border-dashed border-brand/30 text-sm font-semibold text-brand transition-colors hover:bg-brand-soft/60 lg:hidden"
                  >
                    + Add meal
                  </button>
                </li>
              );
            })}
          </ol>
        )}

        {plans.isPending ? <Skeleton lines={3} label="Loading the meal plan" /> : null}
      </div>

      {adding ? (
        <AddMealDialog
          open
          onClose={() => setAdding(null)}
          date={adding.date}
          slot={adding.slot}
          defaultServings={defaultServings}
        />
      ) : null}

      {moving ? (
        <MovePlanDialog
          open
          onClose={() => setMoving(null)}
          plan={moving}
          busy={move.isPending}
          error={move.isError ? messageForError(move.error) : undefined}
          onMove={(date, slot) => {
            const plan = moving;
            move.mutate(
              { plan, date, slot },
              {
                onSuccess: () => setMoving(null),
              },
            );
          }}
        />
      ) : null}
    </AppShell>
  );
}
